import { bridge } from '@sloyd/viewer';
import Color from 'color';
import { delay, guid4, parseTexturedMaterial, ROOT_DB, ROOT_MEDIA, ROOT_OUTPUT } from 'shared-base';
import { getRadius } from '../utils/globals';

let generator: any, model: any;

let materials: any;
let generators: any;

export const init = () => {
  fetch(`${ROOT_DB}/materials.json`)
    .then((res) => res.json())
    .then((data) => {
      materials = data;
    });

  fetch(`${ROOT_DB}/generators.json`)
    .then((res) => res.json())
    .then((data) => {
      generators = data;
    });
};

const getMaterials = () => {
  return materials;
};

const getGenerators = () => {
  return generators;
};

export const getModel = () => {
  return model;
};

export const radial = (backgroundColor: string, intensity: number) => {
  const secondaryColor = Color(backgroundColor)
    .darken(1 - intensity)
    .hex();

  const output: React.CSSProperties = {};

  return `radial-gradient(circle, ${backgroundColor}, ${secondaryColor})`;
};

const pngs = {
  black: `${ROOT_MEDIA}/p-black.png`,
  blue: `${ROOT_MEDIA}/p-blue.png`,
  white: `${ROOT_MEDIA}/p-white.png`,
};

export const defaultTextureMaps: Partial<ITexture> = {
  albedoMapUrl: pngs.white,
  aoMapUrl: pngs.white,
  displacementMapUrl: pngs.black,
  normalMapUrl: pngs.blue,
  metallicMapUrl: pngs.black,
  roughnessMapUrl: pngs.white,
};

export const parseMaterial = (material: Partial<IMaterial>): Partial<IMaterial> => {
  const output = { ...material };

  if (!output.texture) {
    return output;
  }

  output.texture = { ...defaultTextureMaps, ...output.texture };
  return output;
};

export const random = (min: number = 0, max: number = 1) => {
  return Math.random() * (max - min) + min;
};

export const randomInt = (min: number = 0, max: number = 1) => {
  return Math.floor(random(min, max));
};

export const randomArr = (arr: any[]) => {
  const len = arr.length;
  const index = randomInt(0, len);
  return arr[index];
};

const texturesResolution = '256';

export const randomize = (parameters: Json) => {
  const output: Json = {};

  for (let parameter of Object.values(parameters)) {
    const { id, type } = parameter;
    if (id.startsWith('wack') || id === 'LOD') {
      continue;
    }

    switch (type) {
      case 'slider':
        output[id] = roundFixed(Math.random(), 2);
        break;
      case 'picker':
        const { options = [] } = parameter;
        const option = randomArr(options);
        output[id] = option;
        break;
      case 'toggle':
        output[id] = Math.random() > 0.5;
        break;
    }
  }

  return output;
};

export const roundFixed = (value: number, precision: number) => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

export const getWackDelta = (valuesModel: Json, defaultWackParams: Json) => {
  const output: Json = {};

  Object.keys(defaultWackParams).forEach((key) => {
    const defaultValue = defaultWackParams[key];
    const value = valuesModel[key];

    if (value === undefined || value === defaultValue) {
      return;
    }

    output[key] = defaultValue;
  });

  return output;
};

export const randomPack = (generator: IGenerator) => {
  if (!generator || !generator.slots?.active) return;
  const { active } = generator.slots;

  const allMaterials = getMaterials();

  const materialsArr = Object.values(allMaterials) as IMaterial[];
  const s0 = randomArr(materialsArr);
  const s1 = randomArr(materialsArr);
  const s2 = randomArr(materialsArr);
  const s3 = randomArr(materialsArr);

  const m0 = materialsArr.find((m: any) => m.id === s0.id);
  const m1 = materialsArr.find((m: any) => m.id === s1.id);
  const m2 = materialsArr.find((m: any) => m.id === s2.id);
  const m3 = materialsArr.find((m: any) => m.id === s3.id);

  const source: any = {
    0: parseTexturedMaterial(m0, texturesResolution),
    1: parseTexturedMaterial(m1, texturesResolution),
    2: parseTexturedMaterial(m2, texturesResolution),
    3: parseTexturedMaterial(m3, texturesResolution),
  };

  const output: Partial<IMaterialPack> = {};

  active.forEach((slot) => {
    output[slot] = source[slot];
  });

  return output;
};

export const generateModel = async (instanceId: string, id: string): Promise<any> => {
  try {
    const db = getGenerators();

    generator = db.find((g: any) => g.id === id);

    if (!generator) {
      await delay(1000);
      return generateModel(instanceId, id);
    }

    const values = randomize(generator.parameters);
    const materials = randomPack(generator);

    const { slots } = generator ?? {};
    const { active } = slots ?? {};

    model = {
      id: guid4(),
      generatorId: id,
      generatorUrl: `${ROOT_OUTPUT}/${id}.template.json`,
      valuesModel: {
        LOD: 1,
        ...values,
      },
      active,
      materials: { ...materials },
      name: '',
      projectId: guid4(),
      modifiers: {},
    };

    onDone(instanceId);

    return model;
  } catch (err) {
    console.log('err =>', err);
  }
};

export const updateTextarea = () => {
  const textarea = document.querySelector('.code') as HTMLTextAreaElement;
  if (!textarea) return;

  textarea.value = JSON.stringify(model, null, 2);
};

export const onDone = (instanceId: string) => {
  bridge.materialSlotResetAll(instanceId);

  setTimeout(() => {
    updateTextarea();
    // bridge.cameraFocus(instanceId);
  }, 500);
};

export const randomizeAll = (instanceId: string) => {
  model.id = guid4();
  model.valuesModel = randomize(generator.parameters);
  model.materials = randomPack(generator);

  bridge.patchValuesModel(instanceId, model.valuesModel);
  bridge.patchMaterials(instanceId, model.materials as any);

  onDone(instanceId);
};

export const cameraMove = async (instanceId: string) => {
  bridge.cameraMove(instanceId, {
    alpha: -130,
    beta: 65,
    radius: 13,
    duration: 1,
    bezier: [0.25, 0.1, 0.25, 1], // Ease-in-out for a smooth start and stop
  });

  await delay(1500);

  bridge.cameraMove(instanceId, {
    alpha: -400,
    beta: 65,
    radius: 13,
    duration: 0.7, // Faster transition
    bezier: [0.33, 1, 0.68, 1], // Ease-out for a faster exit
  });
};

const SPIN_RPS = 0.15;

export const spin = async (instanceId: string, duration: number, radius: number = getRadius()) => {
  const spinCount = SPIN_RPS * duration;

  bridge.cameraMove(instanceId, {
    alpha: -360 * spinCount,
    beta: 65,
    radius,
    duration,
    bezier: [0.25, 0.1, 0.25, 1], // Ease-in-out for a smooth start and stop
  });
};
